import React from 'react'
import { Loader2 } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import type { SearchResultGroup } from '../types'

interface Props {
  groups: SearchResultGroup[]
  loading: boolean
  hasQuery: boolean
  query: string
  // Called after any result is selected, so the caller can clear the query /
  // close the dropdown.
  onAfterSelect: () => void
}

export const SearchResultsDropdown: React.FC<Props> = ({ groups, loading, hasQuery, query, onAfterSelect }) => {
  const { currentTheme: t } = useTheme()

  if (!hasQuery) return null

  const isEmpty = groups.length === 0

  return (
    <div
      className="absolute inset-x-0 top-full mt-2 rounded-xl border shadow-lg overflow-hidden z-50"
      style={{ backgroundColor: t.secondary, borderColor: t.border }}
    >
      <div className="max-h-[70vh] overflow-y-auto custom-scrollbar py-1.5">
        {isEmpty ? (
          <div className="flex items-center gap-2 px-4 py-3 text-sm" style={{ color: t.mutedText }}>
            {loading ? (
              <>
                <Loader2 size={14} className="animate-spin shrink-0" />
                Searching…
              </>
            ) : (
              <>No results for &ldquo;{query}&rdquo;.</>
            )}
          </div>
        ) : (
          groups.map((group) => (
            <div key={group.category} className="py-1">
              <p
                className="px-4 pt-1.5 pb-1 text-[11px] font-semibold uppercase tracking-wide"
                style={{ color: t.mutedText }}
              >
                {group.category}
              </p>
              {group.items.map((item) => {
                const Icon = item.icon
                return (
                  <div
                    key={item.id}
                    role="button"
                    tabIndex={-1}
                    // mousedown (not click) fires before the input's blur, and
                    // preventDefault keeps focus in the input so this never races
                    // the dropdown unmounting before the selection registers.
                    onMouseDown={(e) => {
                      e.preventDefault()
                      item.onSelect()
                      onAfterSelect()
                    }}
                    className="flex items-center gap-3 px-4 py-2 cursor-pointer transition-colors"
                    style={{ color: t.text }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = t.hoverBackground }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent' }}
                  >
                    <Icon size={16} className="shrink-0" style={{ color: t.mutedText }} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{item.title}</p>
                      {item.subtitle && (
                        <p className="text-xs truncate" style={{ color: t.mutedText }}>{item.subtitle}</p>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          ))
        )}
        {loading && !isEmpty && (
          <div className="flex items-center gap-2 px-4 py-2 text-xs" style={{ color: t.mutedText }}>
            <Loader2 size={12} className="animate-spin shrink-0" />
            Updating CRM results…
          </div>
        )}
      </div>
    </div>
  )
}

export default SearchResultsDropdown
